import { Minus, Plus } from "lucide-react";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getCart, syncCartWithSelections } from "../utils/cart";
import { useNavigate, useLocation } from "react-router-dom";
import { ADDONS } from "../api/addons";
import { useTranslation } from "react-i18next";

const BASE_PACKAGE_PRICE = 350000;
const DEFAULT_QUANTITIES = {
  "Additional User": 1,
  "Permission Setting": 1,
  "Multi-Locations Management": 1,
  "Search Optimization": 1,
  "Stamps / Points Loyalty System": 1,
  "Customized Design": 1,
  "Cards (Gift Cards & Prepaid Loyalty Cards)": 1,
  "Social Media Engagement (PK1/PK2/PK3)": 1,
  "Order Management": 1,
  Reservations: 1,
  "Customer Management": 1,
  "Analytical Dashboard": 1,
  "Customized Menu QR (Hard Print)": 1,
  "Social Media Engagement Months": 1,
  "Permission Setting Quantity": 1,
};

const ADDON_DISPLAY_NAME_BY_TYPE_ID = {
  1: "Additional User",
  2: "Permission Setting",
  3: "Multi-Locations Management",
  4: "Search Optimization",
  5: "Stamps / Points Loyalty System",
  6: "Stamps / Points Loyalty System",
  7: "Customized Design",
  8: "Cards (Gift Cards & Prepaid Loyalty Cards)",
  9: "Cards (Gift Cards & Prepaid Loyalty Cards)",
  10: "Social Media Engagement (PK1/PK2/PK3)",
  11: "Social Media Engagement (PK1/PK2/PK3)",
  12: "Social Media Engagement (PK1/PK2/PK3)",
  13: "Order Management",
  14: "Reservations",
  15: "Customer Management",
  16: "Analytical Dashboard",
  18: "Customized Menu QR (Hard Print)",
};

const QuoteAndFeature = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [checkedItems, setCheckedItems] = useState({});
  console.log("checked item:", checkedItems);

  const [cartItems, setCartItems] = useState([]);
  console.log("cart item:", cartItems);

  const [quantities, setQuantities] = useState(DEFAULT_QUANTITIES);
  console.log("quantity:", quantities);

  const [durations, setDurations] = useState({});
  console.log("durations:", durations);

  const getDisplayName = useCallback((item) => {
    return (
      ADDON_DISPLAY_NAME_BY_TYPE_ID[item?.addonTypeId] ||
      item?.addonName ||
      null
    );
  }, []);

  const cartItemMap = useMemo(() => {
    const map = {};
    cartItems.forEach((item) => {
      const key = getDisplayName(item);
      if (!key) return;

      if (!map[key]) {
        map[key] = {
          amount: 0,
          quantity: 0,
          durationLabel: "",
          isOnetime: !!item.isOnetime,
        };
      }

      map[key].amount += Number(item.price) || 0;
      map[key].quantity += Number(item.quantity) || 1;
      map[key].isOnetime = map[key].isOnetime && !!item.isOnetime;
      if (!map[key].durationLabel && item.durationLabel) {
        map[key].durationLabel = item.durationLabel;
      }
    });
    return map;
  }, [cartItems, getDisplayName]);

  const refreshCartState = useCallback(() => {
    const freshCart = getCart();
    setCartItems(freshCart);

    const nextCheckedItems = {};
    const nextQuantities = { ...DEFAULT_QUANTITIES };
    const nextDurations = {};

    freshCart.forEach((item) => {
      const key = getDisplayName(item);
      console.log("key:", key);

      if (!key) return;

      const hasExistingQuantity = Object.prototype.hasOwnProperty.call(
        nextCheckedItems,
        key,
      );
      nextCheckedItems[key] = true;
      nextQuantities[key] =
        (hasExistingQuantity ? nextQuantities[key] : 0) +
        (Number(item.quantity) || 1);

      if (item.duration) {
        nextDurations[key] = item.duration;
      }

      console.log("next quantity:", nextQuantities);
    });

    setCheckedItems(nextCheckedItems);
    setQuantities(nextQuantities);
    setDurations(nextDurations);
  }, [getDisplayName]);

  useEffect(() => {
    refreshCartState();
  }, [refreshCartState, location.pathname, location.hash]);

  useEffect(() => {
    const handleHashChange = () => {
      refreshCartState();
    };

    const handleStorage = (event) => {
      if (event.key === "shopnest_addon_cart") {
        refreshCartState();
      }
    };

    const handleFocus = () => {
      refreshCartState();
    };

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshCartState]);

  const getPriceForAddon = (addonName) => {
    console.log("Looking for:", addonName);
    console.log(
      "Available ADDONS:",
      ADDONS.map((a) => ({ id: a.addon_type_id, name: a.name })),
    );

    const addonTypeId = Object.entries(ADDON_DISPLAY_NAME_BY_TYPE_ID).find(
      ([id, name]) => name === addonName,
    )?.[0];

    console.log("addonTypeId found:", addonTypeId);

    if (!addonTypeId) {
      return { price: 0, type: "month", durationLabel: "", duration: 1 };
    }

    const addon = ADDONS.find((a) => a.addon_type_id === parseInt(addonTypeId));

    console.log("addon from ADDONS:", addon);

    if (!addon) {
      return { price: 0, type: "month", durationLabel: "", duration: 1 };
    }

    const duration = durations[addonName] || 1;
    const isOnetime = addon.is_onetime;

    let price = 0;
    let durationLabel = "";

    if (isOnetime) {
      price = addon.prices?.onetime_price || 0;
      durationLabel = t("oneTime");
    } else {
      switch (duration) {
        case 1:
          price = addon.prices?.price_1month || 0;
          durationLabel = t("oneMonth");
          break;
        case 3:
          price = addon.prices?.price_3month || 0;
          durationLabel = t("threeMonths");
          break;
        case 6:
          price = addon.prices?.price_6month || 0;
          durationLabel = t("sixMonths");
          break;
        case 12:
          price = addon.prices?.price_12month || 0;
          durationLabel = t("twelveMonths");
          break;
        default:
          price = addon.prices?.price_1month || 0;
          durationLabel = t("oneMonth");
      }
    }

    console.log("Final price:", price, "durationLabel:", durationLabel);

    return {
      price,
      type: isOnetime ? "lifetime" : "month",
      durationLabel,
      duration,
      isOnetime,
    };
  };

  const buildAddonSelections = () => {
    const selections = {};

    const typeIdByName = {};
    Object.entries(ADDON_DISPLAY_NAME_BY_TYPE_ID).forEach(([id, name]) => {
      typeIdByName[name] = parseInt(id);
    });

    Object.entries(checkedItems).forEach(([addonName, isChecked]) => {
      if (isChecked) {
        const addonTypeId = typeIdByName[addonName];
        const cartItem = cartItemMap[addonName];

        selections[addonName] = {
          checked: true,
          addonTypeId: addonTypeId || null,
          quantity: quantities[addonName] || 1,
          duration: durations[addonName] || 1,
          durationLabel:
            cartItem?.durationLabel || `${durations[addonName] || 1} Months`,
          price: cartItem?.amount || 0,
          isOnetime: cartItem?.isOnetime || false,
        };
      }
    });

    return selections;
  };

  const handleProceedToBilling = () => {
    const selections = buildAddonSelections();
    syncCartWithSelections(selections);

    const costs = calculateCosts();
    const billingData = {
      selectedPackage: t("addOns"),
      selectedOption: t("subscription"),
      packagePrice: 0,
      addons: Object.entries(selections).map(([name, selection]) => ({
        name,
        quantity: selection.quantity || 1,
        total: (selection.price || 0) * (selection.quantity || 1),
        durationLabel: selection.durationLabel,
      })),
      subtotal: costs.total,
      tax: costs.total * 0.05,
      discountAmount: costs.discount,
      totalAmount: costs.netTotal,
      billingCycle: 1,
    };

    localStorage.setItem("selectedPlan", JSON.stringify(billingData));
    localStorage.setItem("paymentAmount", costs.netTotal);
    localStorage.setItem("paymentAddonType", "subscription");

    navigate("/billing");
  };

  const handleToggle = (key) => {
    const newChecked = !checkedItems[key];
    setCheckedItems((prev) => ({ ...prev, [key]: newChecked }));
    if (newChecked) {
      setQuantities((prev) => ({ ...prev, [key]: 1 }));
      setDurations((prev) => ({ ...prev, [key]: 1 }));
    } else {
      setQuantities((prev) => ({ ...prev, [key]: 0 }));
      setDurations((prev) => ({ ...prev, [key]: 0 }));
    }
  };

  const getDisplayValue = (addonName) => {
    const isMonthBased = [
      "Search Optimization",
      "Permission Setting",
      "Stamps / Points Loyalty System",
      "Social Media Engagement (PK1/PK2/PK3)",
      "Order Management",
      "Reservations",
      "Customer Management",
      "Analytical Dashboard",
      "Customized Menu QR (Hard Print)",
    ].includes(addonName);

    if (isMonthBased) {
      return durations[addonName] || 0;
    }
    return quantities[addonName] || 0;
  };

  const handleIncrease = (key) => {
    const isMonthBased = [
      "Search Optimization",
      "Permission Setting",
      "Stamps / Points Loyalty System",
      "Social Media Engagement (PK1/PK2/PK3)",
      "Order Management",
      "Reservations",
      "Customer Management",
      "Analytical Dashboard",
      "Customized Menu QR (Hard Print)",
    ].includes(key);

    if (isMonthBased) {
      setDurations((prev) => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
    } else {
      setQuantities((prev) => ({ ...prev, [key]: prev[key] + 1 }));
    }
  };

  const handleDecrease = (key) => {
    const isMonthBased = [
      "Search Optimization",
      "Permission Setting",
      "Stamps / Points Loyalty System",
      "Social Media Engagement (PK1/PK2/PK3)",
      "Order Management",
      "Reservations",
      "Customer Management",
      "Analytical Dashboard",
      "Customized Menu QR (Hard Print)",
    ].includes(key);

    if (isMonthBased) {
      setDurations((prev) => ({
        ...prev,
        [key]: Math.max(0, (prev[key] || 0) - 1),
      }));
    } else {
      setQuantities((prev) => ({ ...prev, [key]: Math.max(0, prev[key] - 1) }));
    }
  };

  const calculateCosts = () => {
    let initialInvestment = BASE_PACKAGE_PRICE;
    let perMonthCost = 0;

    Object.entries(checkedItems).forEach(([addonName, isChecked]) => {
      if (!isChecked) return;

      const priceInfo = getPriceForAddon(addonName);
      const qty = getDisplayValue(addonName);

      if (priceInfo.price > 0) {
        const itemTotal = priceInfo.price * qty;
        if (priceInfo.type === "lifetime") {
          initialInvestment += itemTotal;
        } else {
          perMonthCost += itemTotal;
        }
      }
    });

    const total = initialInvestment + perMonthCost;
    const discount = total * 0.1;
    const netTotal = total - discount;

    return { initialInvestment, perMonthCost, total, discount, netTotal };
  };

  const formatPrice = (price) => {
    return price.toLocaleString() + " MMK";
  };

  const costs = calculateCosts();

  return (
    <div id="code-and-feature" className="mx-20 pt-10 bg-primary-color px-10">
      <h2
        id="title"
        className="text-white text-center text-3xl font-semibold mb-5"
      >
        {t("finalQuoteFeatures")}
      </h2>
      <div id="body" className="flex gap-5">
        <div
          id="1-div"
          className="flex-1 bg-gray-300 rounded-xl px-3 py-3 mb-8 border"
        >
          <h3 className="text-center text-lg font-semibold">
            {t("addOnFeatures")}
          </h3>

          <div id="body" className="space-y-3">
            {/* Additional User */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Additional User"] || false}
                    onChange={() => handleToggle("Additional User")}
                  />
                  <span
                    onClick={() => handleToggle("Additional User")}
                    className="cursor-pointer"
                  >
                    {t("additionalUser")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span></span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Additional User")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Additional User"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Additional User"]
                        ? quantities["Additional User"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Additional User")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Additional User"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Permission Setting */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Permission Setting"] || false}
                    onChange={() => handleToggle("Permission Setting")}
                  />
                  <span
                    onClick={() => handleToggle("Permission Setting")}
                    className="cursor-pointer"
                  >
                    {t("permissionSetting")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Permission Setting")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Permission Setting"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Permission Setting"]
                        ? durations["Permission Setting"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Permission Setting")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Permission Setting"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Permission Setting Quantity (hidden field) */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("quantity")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Permission Setting Quantity")
                      }
                      className="cursor-pointer"
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {quantities["Permission Setting Quantity"]}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Permission Setting Quantity")
                      }
                      className="cursor-pointer"
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Multi-Locations Management */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={
                      checkedItems["Multi-Locations Management"] || false
                    }
                    onChange={() => handleToggle("Multi-Locations Management")}
                  />
                  <span
                    onClick={() => handleToggle("Multi-Locations Management")}
                    className="cursor-pointer"
                  >
                    {t("multiLocationsManagement")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("branches")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Multi-Locations Management")
                      }
                      className="cursor-pointer"
                      disabled={!checkedItems["Multi-Locations Management"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Multi-Locations Management"]
                        ? quantities["Multi-Locations Management"]
                        : 0}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Multi-Locations Management")
                      }
                      className="cursor-pointer"
                      disabled={!checkedItems["Multi-Locations Management"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Optimization */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Search Optimization"] || false}
                    onChange={() => handleToggle("Search Optimization")}
                  />
                  <span
                    onClick={() => handleToggle("Search Optimization")}
                    className="cursor-pointer"
                  >
                    {t("searchOptimization")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Search Optimization")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Search Optimization"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Search Optimization"]
                        ? durations["Search Optimization"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Search Optimization")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Search Optimization"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Stamps / Points Loyalty System */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={
                      checkedItems["Stamps / Points Loyalty System"] || false
                    }
                    onChange={() =>
                      handleToggle("Stamps / Points Loyalty System")
                    }
                  />
                  <span
                    onClick={() =>
                      handleToggle("Stamps / Points Loyalty System")
                    }
                    className="cursor-pointer"
                  >
                    {t("stampsPointsLoyaltySystem")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Stamps / Points Loyalty System")
                      }
                      className="cursor-pointer"
                      disabled={!checkedItems["Stamps / Points Loyalty System"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Stamps / Points Loyalty System"]
                        ? durations["Stamps / Points Loyalty System"]
                        : 0}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Stamps / Points Loyalty System")
                      }
                      className="cursor-pointer"
                      disabled={!checkedItems["Stamps / Points Loyalty System"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Customized Design */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Customized Design"] || false}
                    onChange={() => handleToggle("Customized Design")}
                  />
                  <span
                    onClick={() => handleToggle("Customized Design")}
                    className="cursor-pointer"
                  >
                    {t("customizedDesign")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("quantity")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Customized Design")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Customized Design"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Customized Design"]
                        ? quantities["Customized Design"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Customized Design")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Customized Design"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Cards (Gift Cards & Prepaid Loyalty Cards) */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={
                      checkedItems[
                        "Cards (Gift Cards & Prepaid Loyalty Cards)"
                      ] || false
                    }
                    onChange={() =>
                      handleToggle("Cards (Gift Cards & Prepaid Loyalty Cards)")
                    }
                  />
                  <span
                    onClick={() =>
                      handleToggle("Cards (Gift Cards & Prepaid Loyalty Cards)")
                    }
                    className="cursor-pointer"
                  >
                    {t("giftCardsPrepaidLoyaltyCards")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5"></div>
              </div>
            </div>

            {/* Social Media Engagement */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-3">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={
                      checkedItems["Social Media Engagement (PK1/PK2/PK3)"] ||
                      false
                    }
                    onChange={() =>
                      handleToggle("Social Media Engagement (PK1/PK2/PK3)")
                    }
                  />
                  <span
                    onClick={() =>
                      handleToggle("Social Media Engagement (PK1/PK2/PK3)")
                    }
                    className="cursor-pointer"
                  >
                    {t("socialMediaEngagement")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Social Media Engagement (PK1/PK2/PK3)")
                      }
                      className="cursor-pointer"
                      disabled={
                        !checkedItems["Social Media Engagement (PK1/PK2/PK3)"]
                      }
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Social Media Engagement (PK1/PK2/PK3)"]
                        ? durations["Social Media Engagement (PK1/PK2/PK3)"]
                        : 0}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Social Media Engagement (PK1/PK2/PK3)")
                      }
                      className="cursor-pointer"
                      disabled={
                        !checkedItems["Social Media Engagement (PK1/PK2/PK3)"]
                      }
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media Engagement Months (hidden) */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex flex-1 justify-end gap-5" id="sme-blank">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Social Media Engagement Months")
                      }
                      className="cursor-pointer"
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {quantities["Social Media Engagement Months"]}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Social Media Engagement Months")
                      }
                      className="cursor-pointer"
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Management */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-5">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Order Management"] || false}
                    onChange={() => handleToggle("Order Management")}
                  />
                  <span
                    onClick={() => handleToggle("Order Management")}
                    className="cursor-pointer"
                  >
                    {t("orderManagement")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Order Management")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Order Management"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Order Management"]
                        ? durations["Order Management"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Order Management")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Order Management"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Reservations */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-5">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Reservations"] || false}
                    onChange={() => handleToggle("Reservations")}
                  />
                  <span
                    onClick={() => handleToggle("Reservations")}
                    className="cursor-pointer"
                  >
                    {t("reservations")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Reservations")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Reservations"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Reservations"]
                        ? durations["Reservations"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Reservations")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Reservations"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Management */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-5">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Customer Management"] || false}
                    onChange={() => handleToggle("Customer Management")}
                  />
                  <span
                    onClick={() => handleToggle("Customer Management")}
                    className="cursor-pointer"
                  >
                    {t("customerManagement")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Customer Management")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Customer Management"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Customer Management"]
                        ? durations["Customer Management"]
                        : 0}
                    </span>
                    <button
                      onClick={() => handleIncrease("Customer Management")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Customer Management"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytical Dashboard */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-5">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={checkedItems["Analytical Dashboard"] || false}
                    onChange={() => handleToggle("Analytical Dashboard")}
                  />
                  <span
                    onClick={() => handleToggle("Analytical Dashboard")}
                    className="cursor-pointer"
                  >
                    {t("analyticalDashboard")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() => handleDecrease("Analytical Dashboard")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Analytical Dashboard"]}
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {getDisplayValue("Analytical Dashboard")}
                    </span>
                    <button
                      onClick={() => handleIncrease("Analytical Dashboard")}
                      className="cursor-pointer"
                      disabled={!checkedItems["Analytical Dashboard"]}
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Customized Menu QR */}
            <div id="content" className="flex gap-3 flex-col">
              <div className="flex gap-3 items-center">
                <div className="flex-1 flex gap-5">
                  <input
                    type="checkbox"
                    name=""
                    id=""
                    className="accent-primary-color h-5 w-5 cursor-pointer"
                    checked={
                      checkedItems["Customized Menu QR (Hard Print)"] || false
                    }
                    onChange={() =>
                      handleToggle("Customized Menu QR (Hard Print)")
                    }
                  />
                  <span
                    onClick={() =>
                      handleToggle("Customized Menu QR (Hard Print)")
                    }
                    className="cursor-pointer"
                  >
                    {t("customizedMenuQR")}
                  </span>
                </div>
                <div className="flex flex-1 justify-end gap-5">
                  <span>{t("months")}</span>
                  <div className="flex gap-1 bg-white rounded-sm items-center">
                    <button
                      onClick={() =>
                        handleDecrease("Customized Menu QR (Hard Print)")
                      }
                      className="cursor-pointer"
                      disabled={
                        !checkedItems["Customized Menu QR (Hard Print)"]
                      }
                    >
                      <Minus />
                    </button>
                    <span className="font-bold">
                      {checkedItems["Customized Menu QR (Hard Print)"]
                        ? durations["Customized Menu QR (Hard Print)"]
                        : 0}
                    </span>
                    <button
                      onClick={() =>
                        handleIncrease("Customized Menu QR (Hard Print)")
                      }
                      className="cursor-pointer"
                      disabled={
                        !checkedItems["Customized Menu QR (Hard Print)"]
                      }
                    >
                      <Plus />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="2-div" className="flex-1 bg-white rounded-xl py-3 px-8 mb-8">
          <div id="body" className="py-0 my-0">
            <div id="content" className="flex flex-col">
              <h1 className="text-center my-3 text-xl font-semibold">
                {t("totalCost")}
              </h1>
              <div className="flex flex-col space-y-3 text-sm">
                <div className="flex justify-between items-center text-xl font-semibold">
                  <h2>{t("basicPackage")}</h2>
                  <span>{formatPrice(BASE_PACKAGE_PRICE)}</span>
                </div>
                <hr className="border border-dashed border-gray-400" />
                <div className="grid grid-cols-3 gap-y-4 text-sm items-center">
                  <h3 className="font-semibold">{t("addOnFeatures")}</h3>
                  <span className="text-center font-semibold">{t("qty")}</span>
                  <span className="text-end font-semibold">{t("price")}</span>

                  {Object.keys(checkedItems).map((key) => {
                    if (checkedItems[key]) {
                      const priceInfo = getPriceForAddon(key);
                      const qty = getDisplayValue(key);
                      const unitPrice = priceInfo.price;
                      const totalPrice = unitPrice * qty;
                      const type = priceInfo.type;
                      const durationLabel = priceInfo.durationLabel || "";
                      const qtyText = durationLabel
                        ? `${qty} x ${durationLabel}`
                        : `${qty}`;

                      return (
                        <React.Fragment key={key}>
                          <h3>
                            {key === "Additional User"
                              ? t("additionalUser")
                              : key === "Permission Setting"
                                ? t("permissionSetting")
                                : key === "Multi-Locations Management"
                                  ? t("multiLocationsManagement")
                                  : key === "Search Optimization"
                                    ? t("searchOptimization")
                                    : key === "Stamps / Points Loyalty System"
                                      ? t("stampsPointsLoyaltySystem")
                                      : key === "Customized Design"
                                        ? t("customizedDesign")
                                        : key ===
                                            "Cards (Gift Cards & Prepaid Loyalty Cards)"
                                          ? t("giftCardsPrepaidLoyaltyCards")
                                          : key ===
                                              "Social Media Engagement (PK1/PK2/PK3)"
                                            ? t("socialMediaEngagement")
                                            : key === "Order Management"
                                              ? t("orderManagement")
                                              : key === "Reservations"
                                                ? t("reservations")
                                                : key === "Customer Management"
                                                  ? t("customerManagement")
                                                  : key ===
                                                      "Analytical Dashboard"
                                                    ? t("analyticalDashboard")
                                                    : key ===
                                                        "Customized Menu QR (Hard Print)"
                                                      ? t("customizedMenuQR")
                                                      : key}
                          </h3>
                          <span className="text-center">{qtyText}</span>
                          <span className="text-end">
                            {priceInfo.price === 0
                              ? t("free")
                              : `${totalPrice.toLocaleString()} MMK${
                                  type === "month" ? ` / ${t("duration")}` : ""
                                }`}
                          </span>
                        </React.Fragment>
                      );
                    }
                    return null;
                  })}
                </div>

                <hr className="border border-t border-dashed border-gray-400" />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <h3>{t("initialInvestment")}</h3>
                  <span>{formatPrice(costs.initialInvestment)}</span>
                </div>
                <div className="flex items-center justify-between text-sm font-semibold">
                  <h3>{t("perMonthCost")}</h3>
                  <span>
                    {formatPrice(costs.perMonthCost)}/{t("month")}
                  </span>
                </div>
                <hr className="border border-t border-dashed border-gray-400" />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <h3>{t("total")}</h3>
                  <span>{formatPrice(costs.total)}</span>
                </div>
                <div className="grid grid-cols-3 items-center text-sm font-semibold">
                  <h3>{t("partnershipDiscount")}</h3>
                  <span className="text-center">10%</span>
                  <span className="text-end">
                    {formatPrice(costs.discount)}
                  </span>
                </div>

                <hr className="border border-t border-dashed border-gray-400" />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <h3>{t("netTotal")}</h3>
                  <span></span>
                  <span>{formatPrice(costs.netTotal)}</span>
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleProceedToBilling}
                    className="w-full bg-[#1E2749] text-white font-semibold py-3 px-6 rounded-lg hover:bg-orange-500 transition hover:text-black"
                  >
                    {t("proceedToBilling")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteAndFeature;
